import { Flex, Layout, Typography, Switch } from "antd";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useGetPendingCorrections } from "@hooks";
import { PendingCorrectionsTable } from "../../components/grading/PendingCorrectionsTable";
import { GradingStatusEnum } from "../../types/grading/GradingStatus";
import { useState } from "react";
import { User } from "@types";

interface Props {
  user?: User;
}

const PRIVILEGED_ROLES = ["Admin", "ExamAdmin", "TermAdmin"];

export const PendingCorrectionsPage = ({ user }: Props) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<GradingStatusEnum | undefined>(undefined);

  const roleNames = user?.applicationRoles?.map((role) => role.name) ?? [];
  const isAssessor = roleNames.includes("Assessor");
  const isPrivileged = roleNames.some((name) => name && PRIVILEGED_ROLES.includes(name));
  const canAssign = roleNames.includes("Admin");

  // Assessors land on their own assigned exams by default; privileged users see everything.
  const [onlyMine, setOnlyMine] = useState<boolean>(isAssessor && !isPrivileged);

  const { data, isLoading } = useGetPendingCorrections(statusFilter, { assignedToMe: onlyMine });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Layout>
        <Flex justify="space-between" align="center">
          <div style={{ display: "flex" }}>
            <div
              style={{
                width: 3,
                backgroundColor: "#F1B80E",
                borderRadius: 100,
                marginRight: 12,
              }}
            />
            <Typography.Title level={2} style={{ margin: 0 }}>
              {t("grading.pendingCorrections")}
            </Typography.Title>
          </div>

          {isAssessor && (
            <Flex align="center" gap={8}>
              <Typography.Text>{t("grading.onlyAssignedToMe")}</Typography.Text>
              <Switch checked={onlyMine} onChange={setOnlyMine} />
            </Flex>
          )}
        </Flex>

        <div
          style={{
            boxShadow: "rgba(0, 0, 0, 0.16) 1px 0px 5px",
            borderRadius: "8px",
            marginTop: 12,
            backgroundColor: "white",
            padding: "16px",
          }}
        >
          <PendingCorrectionsTable 
            data={data || []} 
            loading={isLoading}
            canAssign={canAssign}
            onStatusFilterChange={setStatusFilter}
          />
        </div>
      </Layout>
    </motion.div>
  );
};
